import React from 'react';
import { Button } from './ui/Button';

/**
 * Top-level error boundary. Catches render-phase exceptions in the React tree
 * and shows a graceful fallback instead of a white screen. Network errors and
 * async failures still need to be handled per-screen — boundaries don't catch
 * promise rejections or event-handler errors.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] render-phase error', error, info);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    try {
      localStorage.setItem('activeMenu', 'Dashboard');
    } catch {
      // ignore
    }
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="min-h-screen flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-md bg-white/95 backdrop-blur-md rounded-2xl shadow-large border border-slate-200/80 p-8 sm:p-9 ring-1 ring-slate-900/5">
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-rose-50 flex items-center justify-center ring-4 ring-rose-100/80">
              <svg className="w-8 h-8 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-slate-900 tracking-tight">Something went wrong</h2>
            <p className="text-sm text-slate-600 mt-2">
              An unexpected error occurred. You can reload this page or return to the dashboard.
            </p>
            {this.state.error?.message ? (
              <p className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 break-words">
                {String(this.state.error.message)}
              </p>
            ) : null}
          </div>
          <div className="flex flex-col gap-2">
            <Button type="button" variant="primary" size="lg" className="w-full" onClick={this.handleReload}>
              Reload page
            </Button>
            <button
              type="button"
              onClick={this.handleHome}
              className="text-sm text-slate-600 hover:text-slate-900 font-semibold py-2.5 px-4 rounded-xl hover:bg-slate-100/80 transition-colors"
            >
              Back to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
